import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';

import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import TeamLogoPattern from '@mlb/react-patterns/components/TeamLogo/teamLogo.tsx';

import {
    useInAppView,
    useContextUrlPrefix,
    useLang,
    useMediaConfigGame,
} from 'bdvp-common/src/hooks/config';
import { DateUtils } from 'bdvp-common/src/utils';
import { Tracking } from 'bdvp-tracking';

import Image from './Image';

const Header = styled.header`
    position: relative;
    line-height: 0;
`;

const H1 = styled.h1`
    flex: 0 0 90vw;
    margin-bottom: 0;
    font-family: ${({ theme }) => theme.fonts.family.primary};
    font-size: clamp(1rem, 0.6923rem + 1.5385vw, 1.5rem);
    font-weight: 600;
    text-align: center;
    text-shadow: 0px 0px 5px rgb(0 0 0);

    ${media.lgmin`
        flex: 0 0 auto;
    `}
`;

const Strong = styled.strong`
    display: block;
    margin-bottom: clamp(0px, 2vw, 16px);
    font-size: clamp(2rem, 1.3846rem + 3.0769vw, 3rem);
    font-weight: bold;
    line-height: 1;
`;

const Team = styled.span`
    white-space: nowrap;
`;

const TeamLogo = styled(TeamLogoPattern).attrs({
    height: '100px',
    width: '100px',
    darkMode: true,
})`
    display: none;

    ${media.lgmin`
        display: block;
        margin: 0 100px;
        filter: drop-shadow(0px 0px 5px rgb(0 0 0));
        flex: 0 0 100px;
    `}
`;

const Heading = styled.div`
    position: absolute;
    left: 50%;
    top: 50%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    margin: auto;
    transform: translate(-50%,-50%);
`;

const Link = styled.a`
    body & {
        color: ${COLORS.mlbWhite};

        &:hover {
            color: ${COLORS.mlbWhite};
            border-bottom: 1px dotted ${COLORS.linkBlue};
        }
    }
`;

const Date = styled.small`
    display: block;
    margin: 5px 0 0;
    font-size: 80%;

    ${media.mdmin`
        margin-top: 10px;
        font-size: 75%;
    `}
`;

const Hero = (props) => {

    const {
        data,
        loading,
    } = props;

    const { gamePk } = useParams();
    const { linkTemplates = {} } = useMediaConfigGame();
    const [showGamedayWrapLink = true] = useInAppView('gamedayWrapLink');
    const lang = useLang();
    const contextUrlPrefix = useContextUrlPrefix();
    const date = data.gameDate ? DateUtils.formatDate(data.gameDate, lang, 'long') : '';
    const linkTemplate = linkTemplates.gamedayWrap ?? '/gameday/{gamePk}/final/wrap';
    const linkHref = contextUrlPrefix + linkTemplate.replace('{gamePk}', gamePk);

    return (
        <Header>
            <Image
                loading={loading}
                templateUrl={data?.image?.templateUrl}
                alt={data?.image?.altText || data?.image?.title}
            />
            {!loading && (
                <Heading>
                    <TeamLogo teamId={data?.away?.id} />
                    <H1>
                        <Strong>
                            <Team>{data?.away?.shortName}&nbsp;{data?.lineScore?.away?.runs},</Team> <Team>{data?.home?.shortName}&nbsp;{data?.lineScore?.home?.runs}</Team>
                        </Strong>
                        {showGamedayWrapLink
                            ? <Link href={linkHref} onClick={Tracking.trackGameIndexPageGamedayWrapClick}>{data?.headline}</Link>
                            : data?.headline
                        }
                        <Date>{date}</Date>
                    </H1>
                    <TeamLogo teamId={data?.home?.id} />
                </Heading>
            )}
        </Header>
    );

};

Hero.propTypes = {
    loading: PropTypes.bool.isRequired,
    data: PropTypes.object,
};

Hero.defaultProps = {
    data: {},
};

export default Hero;
